import { z } from "zod";
import { AmountMinorSchema } from "./money.js";
/**
 * A bill's horse allocation, expressed as integer basis points (1 bp =
 * 0.01%), never a floating-point percentage
 * (`docs/products/stable.md` "Accounting": "Percentage allocations use
 * fixed-precision amounts or integer basis points and must sum to exactly
 * 100% when percentage allocation is selected — never floating-point
 * percentages"). 10,000 basis points = 100%.
 */
export const BASIS_POINTS_WHOLE = 10_000;
export const BasisPointsSchema = z
    .number()
    .int()
    .min(0)
    .max(BASIS_POINTS_WHOLE);
/**
 * Validates that a set of per-horse basis-point shares sums to exactly
 * 10,000 (100%) — the domain-level half of the invariant (the persistence
 * layer additionally enforces this with a database CHECK/trigger so it
 * cannot be violated by a bug elsewhere or a concurrent write; see
 * `packages/database/src/stable-accounting-persistence.ts`). Integer
 * addition only, so there is no floating-point drift to worry about.
 */
export function basisPointsSumToWhole(shares) {
    return shares.reduce((sum, share) => sum + share, 0) === BASIS_POINTS_WHOLE;
}
/** Splits `count` horses evenly across 10,000 basis points using the largest
 * remainder method, so a "link to N horses with no explicit percentage"
 * bill (`docs/products/stable.md`: "Vet/Farrier bills link to one or more
 * specific horses ... entered manually") still allocates cost by an exact,
 * auditable share per horse rather than leaving it undefined. The first
 * `10000 % count` horses (in the given order) receive one extra basis
 * point so the shares always sum to exactly 10,000. */
export function evenBasisPointSplit(count) {
    if (!Number.isInteger(count) || count < 1) {
        throw new RangeError("count must be a positive integer.");
    }
    const base = Math.floor(BASIS_POINTS_WHOLE / count);
    const remainder = BASIS_POINTS_WHOLE - base * count;
    return Array.from({ length: count }, (_, index) => index < remainder ? base + 1 : base);
}
/**
 * Splits an exact minor-unit amount across a set of basis-point shares that
 * already sum to 10,000, using the largest-remainder method so the parts
 * always sum back to exactly `amountMinor` — integer `bigint` arithmetic
 * only, never floating point (mirrors `money.ts`'s own "exact string/bigint
 * arithmetic" convention). Order of `shares` is preserved in the result.
 */
export function splitAmountByBasisPoints(amountMinor, shares) {
    if (!basisPointsSumToWhole(shares)) {
        throw new RangeError("Basis-point shares must sum to exactly 10,000.");
    }
    const total = BigInt(amountMinor);
    const whole = BigInt(BASIS_POINTS_WHOLE);
    const floors = shares.map((share) => (total * BigInt(share)) / whole);
    const remainders = shares.map((share, index) => total * BigInt(share) - floors[index] * whole);
    let distributed = floors.reduce((sum, value) => sum + value, 0n);
    let leftover = total - distributed;
    // Largest-remainder method: give the leftover minor units, one each, to
    // the shares with the largest fractional remainder, breaking ties by
    // original order — deterministic and never loses or invents a unit.
    const order = shares
        .map((_, index) => index)
        .sort((a, b) => {
        const diff = remainders[b] - remainders[a];
        if (diff > 0n)
            return 1;
        if (diff < 0n)
            return -1;
        return a - b;
    });
    const results = [...floors];
    for (const index of order) {
        if (leftover <= 0n)
            break;
        results[index] = results[index] + 1n;
        leftover -= 1n;
    }
    distributed = results.reduce((sum, value) => sum + value, 0n);
    if (distributed !== total) {
        // Unreachable given the largest-remainder construction above; kept as
        // an explicit invariant check rather than a silent mismatch, since this
        // is the most financially-sensitive code path in the phase.
        throw new RangeError("Split amounts did not sum to the original total.");
    }
    return results.map((value) => {
        const parsed = AmountMinorSchema.safeParse(value.toString());
        if (!parsed.success) {
            throw new RangeError("Split amount is outside the persistable range.");
        }
        return parsed.data;
    });
}
